import {Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AppsService} from '../../../../core/services/apps.service';
import {App, AppId} from '../../../../shared/models/app';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../app/app.state';
import {Subscription} from 'rxjs';
import {makeTileIdMap, Tile, TILE_TYPE_SUPER_FOLDER, TILE_TYPE_SUPER_CANVAS, TileFolderDisplayMode, TileId, TileIdMap} from '../../../../shared/models/tile';
import {SearchService} from '../../../../core/services/search.service';
import {TileGroup} from '../../../../shared/models/tile-group';
import {filter, map} from 'rxjs/operators';
import {TilesService} from '../../../../core/services/tiles.service';
import {TitleBreadcrumbsChangedAction} from '../../../../core/actions/title.actions';
import {UrlService} from '../../../../core/services/url.service';
import {SuperFolderStructure} from '../../../../shared/models/structure';
import {TileComponent} from '../../../components/tile/tile.component';
import {SpinnerComponent} from '../../../components/spinner/spinner.component';
import {NgClass} from '@angular/common';
import {TileFolderComponent} from '../../../components/tile/types/folder/tile-folder.component';
import {CanvasTileDisplayComponent} from '../../../../features/canvas-tiles/display/components/canvas-tile-display/canvas-tile-display.component';
import {Filters} from '../../../../shared/models/filter';
import {FeatureFlagsService, FeatureFlag} from '../../../../core/services/feature-flags.service';
import {filterByMatchingIds, setsHaveSameMembers} from '../../../../shared/utils/tile-filter.util';
import {AppTileFilterHelper} from '../../../../core/services/app-tile-filter.helper';
import {AppTileTree} from '../../../../shared/models/app-tile-tree';

@Component({
  selector: 'motn-dashboard-picture',
  templateUrl: './dashboard-mosaic.component.html',
  styleUrls: ['./dashboard-mosaic.component.scss'],
  imports: [
    TileComponent,
    SpinnerComponent,
    NgClass,
    TileFolderComponent,
    CanvasTileDisplayComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DashboardMosaicComponent implements OnInit, OnDestroy {
  app: App;
  appId: AppId;

  searchTerm: string = '';

  _tiles: TileIdMap = null;
  _groups: TileGroup[] = null;
  _tileOrderFromBackend: TileId[] = [];

  /**
   * Contains tiles that are compatible with the search box contents only
   */
  groupedTiles: Tile[][];
  isLoading = true;
  TILE_TYPE_SUPER_FOLDER = TILE_TYPE_SUPER_FOLDER;
  TILE_TYPE_SUPER_CANVAS = TILE_TYPE_SUPER_CANVAS;

  filters: Filters;
  matchingTileIds: Set<TileId> | null = null;

  private appSearchFilterEnabled = false;

  private appsSub: Subscription;
  private searchSub: Subscription;
  private filtersSub: Subscription;
  private tilesSub: Subscription;
  private sessTilesSub: Subscription;

  private appTileTree: AppTileTree | null = null;


  constructor(
    private store: Store<AppState>,
    private appsService: AppsService,
    private searchService: SearchService,
    private urlService: UrlService,
    private tilesService: TilesService,
    private featureFlagsService: FeatureFlagsService,
  ) {
  }


  ngOnInit(): void {
    this.appId = this.appsService.currentAppId;
    this.appsService.fetchApp(this.appId);
    // Don't call updateTitle() here - wait for app data to load first

    this.appSearchFilterEnabled = this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_APP_SEARCH_FILTER);

    this.subscribeToApps();
    this.subscribeToSearch();
    this.subscribeToTiles();
  }


  ngOnDestroy() {
    this.appsSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.filtersSub?.unsubscribe();
    this.tilesSub?.unsubscribe();
    this.sessTilesSub?.unsubscribe();
  }


  trackGroupBy(index: number, group: Tile[]): string {
    return `${index} - ${group.length}`;
  }


  get hasNoMatchingGroups(): boolean {
    return this.groupedTiles.every(group => group.length === 0);
  }


  private subscribeToApps() {
    this.appsSub = this.store.select('apps').pipe(
      map((state) => {
        return state.apps.find((a) => {
          return a.ID === this.appId;
        });
      }),
      filter(a => !!a),
    ).subscribe(
      (app) => {
        this.app = JSON.parse(JSON.stringify(app));
        this._groups = JSON.parse(JSON.stringify(app.TileGroups));
        if (this._tileOrderFromBackend.length > 0) {
          this.organizeTilesByGroup();
        }
        // Update breadcrumbs only after app data is loaded
        this.updateBreadcrumbs();
      },
    );
  }


  private subscribeToSearch() {
    this.searchSub = this.urlService.searchTerm$.subscribe(
      (t) => {
        this.searchTerm = t;
        this.organizeTilesByGroup();
      },
    );

    this.filtersSub = this.urlService.filters$.subscribe(filters => {
      this.filters = filters;
      this.organizeTilesByGroup();
    });

    this.searchService.enableSearch();
    this.searchService.enableFilters({
      by: {
        devices: false,
        hubs: false,
        owners: false,
        metadata: true,
        tags: this.appSearchFilterEnabled,
      },
    });
  }


  private subscribeToTiles() {
    this.tilesSub = this.store.select('tiles').pipe(
      map((state) => {
        return state.byApp[this.appId];
      }),
      filter(t => !!t),
    ).subscribe(
      (tiles) => {
        this._tiles = tiles;
        this.appTileTree = null;
        if (this._tileOrderFromBackend.length > 0) {
          this.organizeTilesByGroup();
        }
      },
    );

    this.sessTilesSub = this.tilesService.sessUserTiles$.subscribe(() => {
      this.appTileTree = null;
      if (this._tiles !== null && this._groups !== null && (this.searchTerm || this.filters)) {
        this.organizeTilesByGroup();
      }
    });

    this.tilesService.fetchAppTiles$(this.appId).subscribe(
      (tiles) => {
        this._tileOrderFromBackend = tiles.map(t => t.ID);
        // Initialize _tiles and _groups if not set yet (for empty apps)
        if (this._tiles === null) {
          this._tiles = {};
        }
        if (this._groups === null) {
          this._groups = [];
        }
        this.organizeTilesByGroup();
        this.isLoading = false;
        const parentTileIds: TileId[] = tiles.filter(
          (t) => {
            return t.typeSupportsHavingDescendants();
          },
        ).map(
          (t) => {
            return t.ID;
          },
        );

        if (parentTileIds.length > 0) {
          this.tilesService.fetchTilesAndDescendants$(parentTileIds).subscribe();
        }
      },
    );
  }


  private organizeTilesByGroup(): void {
    if (this._tiles === null) {
      return;
    }

    if (this._groups === null) {
      return;
    }

    const newMatchingTileIds = this.computeMatchingTileIds();
    if (!setsHaveSameMembers(newMatchingTileIds, this.matchingTileIds)) {
      this.matchingTileIds = newMatchingTileIds;
    }

    const groupedTiles: Tile[][] = [];

    let tiles: TileIdMap = {...this._tiles};

    if (this.matchingTileIds) {
      tiles = makeTileIdMap(filterByMatchingIds(Object.values(tiles), t => t.ID, this.matchingTileIds));
      this.searchService.setResultQty(Object.values(tiles).length);
    }

    // this will track all the tile IDs accounted for below in this.groupedTiles
    // so that we'll be able to determine afterward which tiles do not appear in
    // this.groups, and thus need to be appended to the "ungrouped" group; this is
    // necessary since any given tile won't appear in this.groups (and the respective
    // row in the database) until the very first time the user uses the tile groups
    // tool after the tile first existed
    const groupedTileTracker: TileId[] = [];

    // organize tiles by group
    for (let g in this._groups) {
      groupedTiles[g] = [];

      // store the original set of tiles here and then reset this.groups[g].Tiles
      // to a brand new array; this process is necessary to strip out tiles that
      // appear in the tile group config but which are no longer linked with this
      // app
      const groupTiles = this._groups[g].Tiles;

      for (let i in groupTiles) {
        let tileId = groupTiles[i];

        // as long as the tile exists in this app's linked tiles, we can go add it
        // to the various "ungrouped" arrays
        if (tiles[tileId]) {
          groupedTileTracker.push(tileId);
          groupedTiles[g].push(tiles[tileId]);
        }
      }
    }

    // brand new apps will need this
    if (groupedTiles.length === 0) {
      groupedTiles.push([]);
    }

    // make sure tiles that have never been saved to a group or custom-ordered appear
    // in the first group ("ungrouped")
    // Use backend order to ensure consistent ordering across config tool and dashboard
    if (this._tileOrderFromBackend.length > 0) {
      // Iterate through backend order and add ungrouped tiles
      for (const tileId of this._tileOrderFromBackend) {
        if (tiles[tileId] && !groupedTileTracker.includes(tileId)) {
          groupedTiles[0].push(tiles[tileId]);
        }
      }
    }

    this.groupedTiles = groupedTiles;
  }


  private computeMatchingTileIds(): Set<TileId> | null {
    if (!this.searchTerm && !this.filters) {
      return null;
    }

    if (this._tiles === null) {
      return null;
    }

    if (!this.appTileTree) {
      this.appTileTree = AppTileFilterHelper.buildTileTree(this._tiles, this.tilesService.sessUserTiles);
    }

    return AppTileFilterHelper.computeFilteredTileIds(
      this._tiles, this.appTileTree, this.searchTerm, this.filters, this.searchService,
    );
  }


  private updateBreadcrumbs() {
    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [
        {
          path: UrlService.makeMosaicDashboardUrl(this.appId),
          text: this.app?.Name || 'Mosaic',
        },
        {
          text: 'Dashboard',
        },
      ],
    ));
  }


  isDataPointsView(tile: Tile): boolean {
    return (tile?.Structure as SuperFolderStructure)?.DisplayMode === TileFolderDisplayMode.DataPoints;
  }
}
